import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Mail, Download as DownloadIcon, DollarSign, FileText, Loader2, Crown, ExternalLink, Copy, Send, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, icon: Icon, tint = 'bg-orange-100 text-orange-700' }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5">
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-medium">{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-xl grid place-items-center ${tint}`}>
            <Icon size={14} />
          </div>
        )}
      </div>
      <div className="font-display font-black text-3xl tracking-tight">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch (e) { return iso; }
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/dashboard`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.status === 403 ? 'forbidden' : 'error'));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;

  if (err === 'forbidden') {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display font-bold text-3xl">Not authorized</h1>
        <p className="text-stone-500 mt-3">You don't have access to the admin dashboard.</p>
        <Link to="/" className="mt-6 inline-block text-orange-600 underline">Back to home</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center text-stone-500">
        <Loader2 className="mx-auto animate-spin mb-3" size={28} />
        Loading dashboard…
      </div>
    );
  }

  const s = data.stats;
  const csvUrl = `${API}/admin/newsletter.csv`;
  const leadsCsvUrl = `${API}/admin/leads.csv`;

  const leadEmails = (data.recent_leads || []).map(l => l.email).filter(Boolean);
  const uniqueLeadEmails = Array.from(new Set(leadEmails));

  const copyLeadEmails = async () => {
    if (uniqueLeadEmails.length === 0) return;
    try {
      await navigator.clipboard.writeText(uniqueLeadEmails.join(', '));
      toast.success(`${uniqueLeadEmails.length} emails copied to clipboard`);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const gmailComposeHref = uniqueLeadEmails.length
    ? `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(uniqueLeadEmails.join(','))}&su=${encodeURIComponent('A quick update from Everything PDF')}`
    : '#';

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">Admin</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Dashboard</h1>
        </div>
        <a
          href={csvUrl}
          data-testid="newsletter-csv-btn"
          className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white px-5 py-2.5 rounded-full text-sm font-medium btn-clay"
        >
          <DownloadIcon size={14} /> Download newsletter CSV
        </a>
      </div>

      {/* STATS GRID */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total users" value={s.total_users} sub={`+${s.new_users_24h} in last 24h`} icon={Users} tint="bg-amber-100 text-amber-800" />
        <StatCard label="Pro users" value={s.pro_users} sub={`${s.total_users > 0 ? Math.round(s.pro_users / s.total_users * 100) : 0}% conversion`} icon={Crown} tint="bg-orange-100 text-orange-700" />
        <StatCard label="Newsletter" value={s.newsletter_total} sub={`+${s.newsletter_recent} in last 30d`} icon={Mail} tint="bg-emerald-100 text-emerald-700" />
        <StatCard label="Revenue" value={`$${s.total_revenue.toFixed(0)}`} sub="paid (lifetime)" icon={DollarSign} tint="bg-sky-100 text-sky-700" />
        <StatCard label="PDFs downloaded" value={s.total_downloads} sub={`+${s.downloads_24h} in 24h`} icon={FileText} tint="bg-rose-100 text-rose-700" />
        <StatCard label="Leads captured" value={s.leads_total || 0} sub={`+${s.leads_recent || 0} in last 30d`} icon={UserPlus} tint="bg-indigo-100 text-indigo-700" />
        <StatCard label="Signups (30d)" value={s.new_users_30d} icon={Users} tint="bg-stone-100 text-stone-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent users */}
        <div className="bg-white border border-stone-200 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl tracking-tight">Recent signups</h2>
            <span className="text-xs text-stone-400">{data.recent_users.length} shown</span>
          </div>
          {data.recent_users.length === 0 ? (
            <p className="text-sm text-stone-500">No users yet. Start marketing! 🚀</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {data.recent_users.map((u, i) => (
                <li key={i} className="py-3 flex items-center gap-3" data-testid={`admin-user-${i}`}>
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-9 h-9 rounded-full" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-stone-200" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                    <div className="text-xs text-stone-500 truncate">{u.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold">
                      {u.plan === 'lifetime' ? <span className="text-orange-600">Lifetime</span> : u.plan === 'pro' ? <span className="text-orange-600">Pro</span> : <span className="text-stone-400">Free</span>}
                    </div>
                    <div className="text-xs text-stone-400">{relTime(u.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top templates */}
        <div className="bg-white border border-stone-200 rounded-3xl p-6">
          <h2 className="font-display font-bold text-xl tracking-tight mb-4">Top downloaded templates</h2>
          {data.top_templates.length === 0 ? (
            <p className="text-sm text-stone-500">No downloads yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_templates.map((t, i) => {
                const max = data.top_templates[0].count || 1;
                const pct = Math.round((t.count / max) * 100);
                return (
                  <li key={i} className="text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium truncate">{t.title || t.template_id}</span>
                      <span className="text-stone-500 ml-2 shrink-0">{t.count}</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-400 to-orange-600" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Leads (free-download email gate) */}
        <div className="bg-white border border-stone-200 rounded-3xl p-6 lg:col-span-2" data-testid="admin-leads-section">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-display font-bold text-xl tracking-tight">Leads (free-download email gate)</h2>
              <p className="text-xs text-stone-500 mt-1">
                {uniqueLeadEmails.length} unique emails · {(data.recent_leads || []).length} recent captures
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={copyLeadEmails}
                disabled={uniqueLeadEmails.length === 0}
                data-testid="leads-copy-emails-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 hover:border-stone-300 text-xs font-medium text-stone-700 disabled:opacity-40"
              >
                <Copy size={12} /> Copy emails
              </button>
              <a
                href={gmailComposeHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={uniqueLeadEmails.length === 0}
                data-testid="leads-compose-gmail-btn"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 hover:border-stone-300 text-xs font-medium text-stone-700 ${uniqueLeadEmails.length === 0 ? 'pointer-events-none opacity-40' : ''}`}
              >
                <Send size={12} /> Compose in Gmail
              </a>
              <a
                href={leadsCsvUrl}
                data-testid="leads-csv-btn"
                className="inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white px-4 py-1.5 rounded-full text-xs font-medium"
              >
                <DownloadIcon size={12} /> Export CSV
              </a>
            </div>
          </div>

          {(data.recent_leads || []).length === 0 ? (
            <p className="text-sm text-stone-500">No leads captured yet. Share a free template link to start collecting emails.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3">Document</th>
                    <th className="text-right py-2 px-3">Downloads</th>
                    <th className="text-right py-2 px-3">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent_leads || []).map((l, i) => (
                    <tr key={i} className="border-t border-stone-100" data-testid={`admin-lead-${i}`}>
                      <td className="py-2 px-3 font-medium">{l.email}</td>
                      <td className="py-2 px-3 text-stone-600 text-xs">{l.document_name || l.template_id || '—'}</td>
                      <td className="py-2 px-3 text-right text-xs">{l.download_count || 1}</td>
                      <td className="py-2 px-3 text-right text-xs text-stone-500">{relTime(l.last_seen_at || l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Newsletter list */}
        <div className="bg-white border border-stone-200 rounded-3xl p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl tracking-tight">Newsletter subscribers</h2>
            <a href={csvUrl} className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1">
              Download all <ExternalLink size={11} />
            </a>
          </div>
          {data.recent_subscribers.length === 0 ? (
            <p className="text-sm text-stone-500">No subscribers yet. Promote your blog! 📨</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-stone-500">
                  <tr><th className="text-left py-2 px-3">Email</th><th className="text-left py-2 px-3">Source</th><th className="text-right py-2 px-3">Subscribed</th></tr>
                </thead>
                <tbody>
                  {data.recent_subscribers.map((sub, i) => (
                    <tr key={i} className="border-t border-stone-100" data-testid={`admin-sub-${i}`}>
                      <td className="py-2 px-3 font-medium">{sub.email}</td>
                      <td className="py-2 px-3 text-stone-500 text-xs">{sub.source || '—'}</td>
                      <td className="py-2 px-3 text-right text-xs text-stone-500">{relTime(sub.subscribed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent payments */}
        <div className="bg-white border border-stone-200 rounded-3xl p-6 lg:col-span-2">
          <h2 className="font-display font-bold text-xl tracking-tight mb-4">Recent payments</h2>
          {data.recent_payments.length === 0 ? (
            <p className="text-sm text-stone-500">No paid transactions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3">Plan</th>
                    <th className="text-right py-2 px-3">Amount</th>
                    <th className="text-right py-2 px-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_payments.map((p, i) => (
                    <tr key={i} className="border-t border-stone-100">
                      <td className="py-2 px-3 font-medium">{p.email}</td>
                      <td className="py-2 px-3"><span className="text-[10px] uppercase tracking-wider font-bold text-orange-600">{p.plan_id}</span></td>
                      <td className="py-2 px-3 text-right">${Number(p.amount).toFixed(2)}</td>
                      <td className="py-2 px-3 text-right text-xs text-stone-500">{relTime(p.applied_at || p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
